import { useContext, useState, useEffect } from "react";
import { UserDataContext } from "../components/UserDataContext";
import VN from "../components/VN";
import { useParams } from "react-router-dom";
import { apiRequest } from "../services/apiService";
import {
  Container,
  Typography,
  Paper,
  Box,
  Avatar,
  Divider,
  Chip,
  Stack,
} from "@mui/material";

import LocationOnIcon from "@mui/icons-material/LocationOn";
import PhoneIcon from "@mui/icons-material/Phone";
import WorkIcon from "@mui/icons-material/Work";
import ChecklistIcon from "@mui/icons-material/Checklist";
import MapIcon from "@mui/icons-material/Map";
import FaceIcon from "@mui/icons-material/Face";
import LanguageIcon from "@mui/icons-material/Language";
import CakeIcon from "@mui/icons-material/Cake";
import { API_URL } from "../config/api";

export default function Portfolio() {
  const { userData } = useContext(UserDataContext);
  const [loadingData, setLoadingData] = useState(true);
  //console.log("userData:", userData);
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [user, setUser] = useState({});
  const [cities, setCities] = useState([]);
  const [possibilities, setPossibilities] = useState({});
  const [qualifications, setQualifications] = useState({});
  const [experience, setExperience] = useState({});
  const [languages, setLanguages] = useState({});
  const [userText, setUserText] = useState();
  const sectionStyle = {
    p: 3,
    borderRadius: 4,
    height: "100%",
    display: "flex",
    flexDirection: "column",
    position: "relative",
    overflow: "hidden",
    boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
    border: "1px solid #f0f0f0",
    transition: "all .2s ease",
    "&:hover": {
      boxShadow: "0 10px 35px rgba(0,0,0,0.08)",
      transform: "translateY(-2px)",
    },
  };

  const { id } = useParams();
  const photoUrl = `${API_URL}/get-user-photo/${id}`;

  useEffect(() => {
    const fetchUser = async () => {
      if (!userData?.userId) return;
      setLoadingData(true);
      try {
        const response = await apiRequest(`getuser/${id}`, "GET");
        //console.log("api response: ", response);
        if (response && response.length > 0) {
          setUser(response[0]);
        }
      } finally {
        setLoadingData(false);
      }
    };

    fetchUser();
  }, [userData]);

  useEffect(() => {
    const fetchUserSelections = async () => {
      if (!userData?.userId) return;
      setLoadingData(true);
      try {
        const response = await apiRequest(
          `get-user-possibilities/${id}`,
          "GET",
        );
        if (response && response.length > 0) {
          setPossibilities(response[0]);
        }
      } finally {
        setLoadingData(false);
      }
    };

    fetchUserSelections();
  }, [userData]);

  useEffect(() => {
    const fetchUserSelections = async () => {
      if (!userData?.userId) return;
      setLoadingData(true);
      try {
        const response = await apiRequest(
          `get-user-qualifications/${id}`,
          "GET",
        );

        if (response && response.success) {
          setQualifications(response);
        }
      } catch (error) {
        console.error("Błąd pobierania kwalifikacji:", error);
      } finally {
        setLoadingData(false);
      }
    };

    fetchUserSelections();
  }, [userData, id]);

  useEffect(() => {
    const fetchUserSelections = async () => {
      if (!userData?.userId) return;

      setLoadingData(true);
      try {
        const response = await apiRequest(`get-user-experience/${id}`, "GET");
        console.log("Experience: ", userData);

        if (response && response.success) {
          setExperience(response);
        }
      } catch (error) {
        console.error("Błąd pobierania:", error);
      } finally {
        setLoadingData(false);
      }
    };

    fetchUserSelections();
  }, [userData, id]);

  useEffect(() => {
    const fetchUserSelections = async () => {
      if (!id) return;

      setLoadingData(true);
      try {
        const response = await apiRequest(
          `get-user-possibilities/${id}`,
          "GET",
        );

        if (response.success) {
          setPossibilities(response);
          if (Array.isArray(response.selectedIds)) {
            setSelectedItems(new Set(response.selectedIds.map(Number)));
          }
        }
      } catch (error) {
        console.error("Błąd pobierania:", error);
      } finally {
        setLoadingData(false);
      }
    };
    fetchUserSelections();
  }, [id]);

  useEffect(() => {
    const fetchUserSelections = async () => {
      if (!id) return;

      setLoadingData(true);
      try {
        const response = await apiRequest(`get-user-languages/${id}`, "GET");

        if (response.success) {
          console.log("Languages: ", response);
          setLanguages(response);
        }
      } catch (error) {
        console.error("Błąd pobierania:", error);
      } finally {
        setLoadingData(false);
      }
    };
    fetchUserSelections();
  }, [id]);

  useEffect(() => {
    const fetchUserSelections = async () => {
      if (!id) return;
      setLoadingData(true);
      try {
        const response = await apiRequest(`get-user-cities/${id}`, "GET");
        if (response.success) {
          setCities(response.cities);
        }
      } catch (error) {
        console.error("Błąd pobierania:", error);
      } finally {
        setLoadingData(false);
      }
    };
    fetchUserSelections();
  }, [id]);

  useEffect(() => {
    const fetchUserText = async () => {
      if (!userData?.userId) return;

      setLoadingData(true);

      try {
        const response = await apiRequest(`get-user-text/${id}`, "GET");
        if (response && response.length > 0) {
          setUserText(response[0]);
        }
      } finally {
        setLoadingData(false);
      }
    };

    fetchUserText();
  }, [userData]);

  const groupedCities = cities.reduce((acc, city) => {
    if (!acc[city.voivodeship]) {
      acc[city.voivodeship] = [];
    }
    acc[city.voivodeship].push(city.city);
    return acc;
  }, {});

  const getAgeLabel = (age) => {
    if (age === 1) return "rok";
    if (age % 10 >= 2 && age % 10 <= 4 && (age % 100 < 12 || age % 100 > 14)) {
      return "lata";
    }
    return "lat";
  };

  const currentYear = new Date().getFullYear();
  const age = user.birthyear ? currentYear - user.birthyear : null;
  return (
    <div>
      <VN userData={userData} />
      <Container maxWidth="lg">
        <Box mb={6} textAlign="center">
          <Typography
            variant="h3"
            fontWeight={800}
            gutterBottom
            sx={{ color: "#1a1a1a" }}
          >
            Profil zawodowy opiekunki
          </Typography>
        </Box>

        <Box
          sx={{
            display: "grid",
            gap: 4,
            gridTemplateColumns: {
              xs: "1fr",
              md: "1fr 1fr",
            },
          }}
        >
          {/* LEWA KOLUMNA: PROFIL I KONTAKT */}
          <Box>
            <Paper sx={sectionStyle}>
              {/* Dekoracyjny pasek na górze karty */}
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  height: "80px",
                  bgcolor: "primary.main",
                  opacity: 0.1,
                  zIndex: 0,
                }}
              />

              <Box position="relative" zIndex={1}>
                <Avatar
                  src={photoUrl}
                  sx={{
                    width: 150,
                    height: 150,
                    margin: "0 auto 20px",
                    boxShadow: "0 8px 25px rgba(0,0,0,0.15)",
                    border: "4px solid white",
                  }}
                  imgProps={{ crossOrigin: "use-credentials" }}
                ></Avatar>

                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  gap={1}
                >
                  <Typography variant="h5" fontWeight={700}>
                    {user.fullname}
                  </Typography>

                  {age && (
                    <Chip
                      icon={<CakeIcon />}
                      label={`${age} ${getAgeLabel(age)}`}
                      variant="outlined"
                      sx={{
                        borderRadius: 2,
                        fontWeight: 500,
                        borderColor: "primary.light",
                        bgcolor: "rgba(25, 118, 210, 0.04)",
                      }}
                    />
                  )}
                </Box>
                <Stack spacing={2} mt={4} alignItems="flex-start">
                  <Box display="flex" alignItems="center" gap={1.5}>
                    <LocationOnIcon color="primary" />
                    <Typography variant="body1">
                      <strong>Miejscowość:</strong> {user.city || "Nie podano"}
                    </Typography>
                  </Box>
                  <Box display="flex" alignItems="center" gap={1.5}>
                    <PhoneIcon color="primary" />
                    <Typography variant="body1">
                      <strong>Telefon:</strong> {user.phone || "Brak numeru"}
                    </Typography>
                  </Box>
                  <Box display="flex" alignItems="center" gap={1.5}>
                    <FaceIcon color="primary" />
                    <Typography variant="body1">
                      <strong>Krótko o mnie:</strong>{" "}
                      {userText || "Brak informacji..."}
                    </Typography>
                  </Box>
                </Stack>
                <Box display="flex" alignItems="flex-start" gap={1.5}>
                  <LanguageIcon color="primary" />
                  <Box>
                    <Typography variant="body1">
                      <strong>Języki:</strong>
                    </Typography>

                    {languages?.data && languages.data.length > 0 ? (
                      <Stack
                        direction="row"
                        spacing={1}
                        flexWrap="wrap"
                        useFlexGap
                        mt={1}
                      >
                        {languages.data.map((lang, index) => (
                          <Chip
                            key={index}
                            label={`${lang.language} (${lang.level_name})`}
                            variant="outlined"
                            sx={{
                              borderRadius: 2,
                              fontWeight: 500,
                              borderColor: "primary.light",
                              bgcolor: "rgba(25, 118, 210, 0.04)",
                            }}
                          />
                        ))}
                      </Stack>
                    ) : (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontStyle: "italic" }}
                      >
                        Brak informacji o językach.
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Box>
            </Paper>
          </Box>

          {/* PRAWA KOLUMNA: ZAKRES CZYNNOŚCI */}
          <Box>
            <Paper sx={sectionStyle}>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <ChecklistIcon color="primary" />
                <Typography variant="h5" fontWeight={600}>
                  Co mogę zrobić?
                </Typography>
              </Box>
              <Divider sx={{ mb: 3 }} />

              {/* Tutaj mapujesz dane z bazy, jeśli masz je w tablicy, np. userData.services */}
              <Box display="flex" flexWrap="wrap" gap={1}>
                {possibilities?.activity &&
                possibilities.activity.length > 0 ? (
                  possibilities.activity.map((item, index) => (
                    <Chip
                      key={index}
                      label={item}
                      variant="outlined"
                      sx={{
                        borderRadius: 2,
                        fontWeight: 500,
                        p: 1,
                        borderColor: "primary.light", // Opcjonalnie: nadaj im kolor
                        bgcolor: "rgba(25, 118, 210, 0.04)",
                      }}
                    />
                  ))
                ) : (
                  // Co wyświetlić, gdy użytkownik nie ma wybranych żadnych czynności
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontStyle: "italic" }}
                  >
                    Brak określonego zakresu czynności.
                  </Typography>
                )}
              </Box>
            </Paper>
          </Box>

          {/* DOLNA KOLUMNA LEWA: MIASTA PRACY */}
          <Box>
            <Paper sx={sectionStyle}>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <MapIcon color="primary" />
                <Typography variant="h5" fontWeight={600}>
                  W jakich miastach mogę pracować
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="body1" mb={2}>
                Jestem osobą mobilną i mogę podjąć pracę w następujących
                lokalizacjach:
              </Typography>
              {cities && cities.length > 0 ? (
                Object.entries(groupedCities).map(([voivodeship, cityList]) => (
                  <Box key={voivodeship} mb={2}>
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 600, mb: 1 }}
                    >
                      {voivodeship}
                    </Typography>

                    <Stack
                      direction="row"
                      spacing={1}
                      flexWrap="wrap"
                      useFlexGap
                    >
                      {cityList.map((city, index) => (
                        <Chip
                          key={index}
                          label={city}
                          color="primary"
                          variant="outlined"
                          sx={{
                            borderRadius: 2,
                            fontWeight: 500,
                            p: 1,
                            borderColor: "primary.light", // Opcjonalnie: nadaj im kolor
                            bgcolor: "rgba(25, 118, 210, 0.04)",
                          }}
                        />
                      ))}
                    </Stack>
                  </Box>
                ))
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Brak określonych lokalizacji pracy.
                </Typography>
              )}
            </Paper>
          </Box>

          {/* DOLNA KOLUMNA PRAWA: DOŚWIADCZENIE */}
          <Box>
            <Paper sx={sectionStyle}>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <WorkIcon color="primary" />
                <Typography variant="h5" fontWeight={600}>
                  Doświadczenie
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <Box display="flex" flexWrap="wrap" gap={1}>
                {experience?.name && experience.name.length > 0 ? (
                  experience.name.map((item, index) => (
                    <Chip
                      key={index}
                      label={item}
                      variant="outlined"
                      sx={{
                        borderRadius: 2,
                        fontWeight: 500,
                        p: 1,
                        borderColor: "primary.light", // Opcjonalnie: nadaj im kolor
                        bgcolor: "rgba(25, 118, 210, 0.04)",
                      }}
                    />
                  ))
                ) : (
                  // Co wyświetlić, gdy użytkownik nie ma wybranych żadnych czynności
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontStyle: "italic" }}
                  >
                    Brak doświadczenia..
                  </Typography>
                )}
              </Box>
            </Paper>
          </Box>

          <Box>
            <Paper sx={sectionStyle}>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <WorkIcon color="primary" />
                <Typography variant="h5" fontWeight={600}>
                  Kwalifikacje
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <Box display="flex" flexWrap="wrap" gap={1}>
                {qualifications?.description &&
                qualifications.description.length > 0 ? (
                  qualifications.description.map((item, index) => (
                    <Chip
                      key={index}
                      label={item}
                      variant="outlined"
                      sx={{
                        borderRadius: 2,
                        fontWeight: 500,
                        p: 1,
                        borderColor: "primary.light", // Opcjonalnie: nadaj im kolor
                        bgcolor: "rgba(25, 118, 210, 0.04)",
                      }}
                    />
                  ))
                ) : (
                  // Co wyświetlić, gdy użytkownik nie ma wybranych żadnych czynności
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontStyle: "italic" }}
                  >
                    Brak kwalifikacji..
                  </Typography>
                )}
              </Box>
            </Paper>
          </Box>
        </Box>
      </Container>
    </div>
  );
}
